/**
 * seo/routes.js — single source of truth for every INDEXABLE public route.
 *
 * Dependency-free (no React/JSX) so it is importable from both the browser bundle
 * (App.jsx builds its per-route <head> table from CORE_ROUTES) and the Node
 * build-time script (scripts/prerender.mjs), which uses INDEXABLE_ROUTES to emit
 * one static prerendered HTML file per route AND to generate public/sitemap.xml.
 *
 * Because the sitemap and the prerendered pages are both derived from this list,
 * they can no longer drift from the app's real routes. Add a public page here (or
 * in marketing/pages.js / content/blog) and it flows into both automatically.
 *
 * App-shell routes (/app, /admin, /invite/*) are intentionally absent: they are
 * noindex + blocked in robots.txt and must never be prerendered or listed.
 */
import { MARKETING_PAGES } from '../components/marketing/pages.js';
import { POSTS } from '../content/blog/index.js';
import { INTENT_ROUTES } from '../content/intents/index.js';
import { ALL_LIVE, LIVE_NAMES, SOON_NAMES } from '../site/siteConfig.js';

export const SITE_URL = 'https://personadraft.com';

// Bumped when marketing/legal copy meaningfully changes; feeds sitemap <lastmod>.
// Blog posts carry their own date instead. Keeping this a constant (not the build
// date) means re-running the build produces an identical sitemap — no churn.
export const SITE_LASTMOD = '2026-10-29';

// Core marketing/legal routes + blog index. `type` drives the prerendered JSON-LD.
export const CORE_ROUTES = [
  { path: '/',
    title: 'PersonaDraft — Sound like yourself, everywhere',
    description: 'Most AI guesses how you sound. PersonaDraft knows exactly: a behavioral engine that maps your voice, learns it over time, and writes as you — across email, chat, and every social surface. Insert, never send.',
    changefreq: 'weekly', priority: 1.0, type: 'home' },
  { path: '/how-it-works',
    title: 'How PersonaDraft works — See it. Then see why it’s you.',
    description: 'One intention, written by one person on every surface. Then the machine behind it: eight behavioral axes, one exact coordinate, and every draft compiled from it and measured against you.',
    changefreq: 'monthly', priority: 0.9, type: 'page' },
  { path: '/waitlist',
    title: 'Join the waitlist — PersonaDraft | Every surface next',
    description: ALL_LIVE
      ? `PersonaDraft is live on ${LIVE_NAMES}.`
      : `PersonaDraft is live in ${LIVE_NAMES} today. Join the waitlist for ${SOON_NAMES}: same mark, same window, same you.`,
    changefreq: 'monthly', priority: 0.4, type: 'page' },
  { path: '/pricing',
    title: 'Pricing — PersonaDraft | Everything, unlimited, $4.99',
    description: 'Every voice, every dial, your own trained voice, no cap on emails. Thirty days free, no card — then Pro at $4.99 a month. Business adds the compliance layer at $19.99 per seat.',
    changefreq: 'weekly', priority: 0.9, type: 'page' },
  { path: '/blog',
    title: 'The PersonaDraft Blog — Writing that lands',
    description: 'Practical, occasionally contrarian notes on tone, workplace communication and the behavioral science behind a message that gets a yes.',
    changefreq: 'weekly', priority: 0.7, type: 'collection' },
  { path: '/privacy',
    title: 'Privacy Policy — PersonaDraft',
    description: 'How PersonaDraft handles your data: names, emails, phone and account numbers are replaced with tokens in your browser before anything is sent.',
    changefreq: 'monthly', priority: 0.3, type: 'page' },
  { path: '/terms',
    title: 'Terms of Service — PersonaDraft',
    description: 'The terms governing your use of PersonaDraft, the behavioral AI email writer for Gmail.',
    changefreq: 'monthly', priority: 0.3, type: 'page' },
];

// Blog posts → routes (title/description/date come from each post module).
export const BLOG_ROUTES = POSTS.map((p) => ({
  path: `/blog/${p.slug}`,
  title: `${p.title} | Persona Blog`,
  heading: p.title, // clean title (no suffix) for the article's JSON-LD headline
  description: p.description,
  changefreq: 'monthly',
  priority: 0.6,
  lastmod: p.date,
  datePublished: p.date,
  type: 'article',
}));

// Marketing pages default to type 'page'.
const MARKETING_ROUTES = MARKETING_PAGES.map((p) => ({ type: 'page', ...p }));

// Every route that should be crawled, indexed, prerendered, and in the sitemap.
export const INDEXABLE_ROUTES = [
  ...CORE_ROUTES, ...MARKETING_ROUTES, ...BLOG_ROUTES, ...INTENT_ROUTES,
];

// path → { title, description, path } for App.jsx's per-route <head>.
export const CORE_HEAD = Object.fromEntries(
  CORE_ROUTES.map((r) => [r.path, { title: r.title, description: r.description, path: r.path }])
);
